/**
 * Finance Review queue (FIN-11) — the post-import triage hub (design §3.3).
 *
 * Three collapsible sections, each with a count badge:
 *   1. Suggested transfers — confirm/reject; sibling suggestions for the same
 *      leg collapse into ONE chooser card ("which is the counterpart?").
 *   2. Agent suggestions — finance.categorize / finance.transfer_match /
 *      finance.alert. Accept is CLIENT-ORCHESTRATED: apply the finance change
 *      first, then record the accept (record-only per ADR 0034). On apply
 *      failure the suggestion stays pending and the error surfaces.
 *   3. Uncategorized — non-leg transactions grouped by normalized description;
 *      one category combobox + "apply to group (n)" + the FIN-10 rule affordance.
 *
 * Keyboard-first (mirrors the Tasks list mental model): j / k move focus across
 * all items in the open sections, Enter = accept/confirm the focused item, x =
 * reject/dismiss. The focused item shows a ring; the handler is suppressed while
 * a dialog or a form control is focused so typing in a combobox is unaffected.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@k-os/ui';
import {
  useFinanceTransfers,
  useConfirmTransfer,
  useRejectTransfer,
  useCreateTransfer,
  useFinanceSuggestions,
  useAcceptFinanceSuggestion,
  useRejectFinanceSuggestion,
  usePatchFinanceTransaction,
  useBulkCategorizeTransactions,
  useFinanceTransactions,
  useFinanceCategories,
  useFinanceReviewCounts,
  type FinanceTransferDto,
  type FinanceSuggestionDto,
  type FinanceTransactionDto,
  type FinanceCategoryDto,
} from '../../api/finance';
import {
  formatEur,
  buildCategoryOptions,
  financeErrorMessage,
  type CategoryOption,
} from './finance-format';
import { CategoryPicker } from '../../components/finance/CategoryPicker';
import { RuleFromCategorizationDialog, type RuleSeed } from '../../components/finance/RuleFromCategorizationDialog';
import styles from './finance.module.css';

// ─── Focusable-item model (keyboard nav) ──────────────────────────────────────
// Every actionable card across the open sections gets a string key. j/k move
// through this ordered list; Enter / x dispatch to the focused item's handlers.

type ItemAction = {
  /** Enter — accept / confirm. */
  primary?: (() => void) | undefined;
  /** x — reject / dismiss. */
  secondary?: (() => void) | undefined;
};

export function FinanceReviewScreen() {
  const transfersQ = useFinanceTransfers('suggested');
  const suggestionsQ = useFinanceSuggestions();
  const uncategorizedQ = useFinanceTransactions({
    uncategorized: true,
    transferLegs: 'exclude',
    limit: 500,
  });
  const categoriesQ = useFinanceCategories();
  const countsQ = useFinanceReviewCounts();

  const categoryOptions = useMemo(
    () => buildCategoryOptions(categoriesQ.data ?? []),
    [categoriesQ.data],
  );

  const transfers = useMemo(() => transfersQ.data ?? [], [transfersQ.data]);
  const suggestions = suggestionsQ.data ?? [];
  const uncategorized = useMemo(
    () => uncategorizedQ.data?.transactions ?? [],
    [uncategorizedQ.data],
  );

  // Collapsible state — open by default.
  const [open, setOpen] = useState({ transfers: true, suggestions: true, uncategorized: true });

  // Group transfer suggestions by leg overlap (chooser cards) ────────────────
  const transferGroups = useMemo(() => groupTransfersByLeg(transfers), [transfers]);

  // Group uncategorized by normalized description ────────────────────────────
  const uncategorizedGroups = useMemo(
    () => groupByNormalizedDescription(uncategorized),
    [uncategorized],
  );

  // ─── Keyboard focus across all open sections ────────────────────────────
  const [focusKey, setFocusKey] = useState<string | null>(null);
  // Registry of item keys (in DOM order) + their actions, rebuilt each render
  // by the child cards via register().
  const itemsRef = useRef<Array<{ key: string; action: ItemAction }>>([]);
  itemsRef.current = [];
  const register = useCallback((key: string, action: ItemAction) => {
    itemsRef.current.push({ key, action });
  }, []);

  const ruleSeedState = useState<RuleSeed | null>(null);
  const [, setRuleDialog] = ruleSeedState;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Don't hijack typing in inputs / selects / editable fields.
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        return;
      }
      const items = itemsRef.current;
      if (items.length === 0) return;
      const idx = items.findIndex((i) => i.key === focusKey);
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        const next = idx < 0 ? 0 : Math.min(items.length - 1, idx + 1);
        setFocusKey(items[next]!.key);
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = idx < 0 ? 0 : Math.max(0, idx - 1);
        setFocusKey(items[prev]!.key);
      } else if (e.key === 'Enter') {
        if (idx >= 0) {
          e.preventDefault();
          items[idx]!.action.primary?.();
        }
      } else if (e.key === 'x') {
        if (idx >= 0) {
          e.preventDefault();
          items[idx]!.action.secondary?.();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focusKey]);

  const counts = countsQ.data;
  const transferCount = counts?.suggestedTransfers ?? transfers.length;
  const suggestionCount = counts?.pendingSuggestions ?? suggestions.length;
  const uncategorizedCount = counts?.uncategorized ?? uncategorized.length;
  const totalPending = transferCount + suggestionCount + uncategorizedCount;

  const loading =
    transfersQ.isLoading || suggestionsQ.isLoading || uncategorizedQ.isLoading || categoriesQ.isLoading;
  const anyError =
    transfersQ.isError || suggestionsQ.isError || uncategorizedQ.isError || categoriesQ.isError;
  const firstError =
    transfersQ.error ?? suggestionsQ.error ?? uncategorizedQ.error ?? categoriesQ.error;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Review</h2>
          <div className={styles.subtitle}>
            Post-import triage. Confirm transfers, accept agent suggestions, and clear
            uncategorized transactions. <kbd className="kos-kbd">j</kbd>/<kbd className="kos-kbd">k</kbd> move,{' '}
            <kbd className="kos-kbd">Enter</kbd> accept, <kbd className="kos-kbd">x</kbd> reject.
          </div>
        </div>
      </header>

      {loading ? (
        <div className={styles.muted}>Loading…</div>
      ) : anyError ? (
        <div className={styles.error}>{financeErrorMessage(firstError)}</div>
      ) : totalPending === 0 ? (
        <div className={styles.empty}>
          <strong>Inbox zero.</strong> Nothing to review — every transfer is confirmed, every
          suggestion handled, and every transaction categorized. Nicely done.
        </div>
      ) : (
        <div className={styles.reviewSections}>
          {/* ─── Suggested transfers ──────────────────────────────────── */}
          <ReviewSection
            title="Suggested transfers"
            count={transferGroups.length}
            open={open.transfers}
            onToggle={() => setOpen((o) => ({ ...o, transfers: !o.transfers }))}
            empty="No transfers awaiting your decision."
          >
            {transferGroups.map((group) => (
              <TransferGroupCard
                key={group.key}
                group={group}
                focusKey={focusKey}
                onFocus={setFocusKey}
                register={register}
              />
            ))}
          </ReviewSection>

          {/* ─── Agent suggestions ────────────────────────────────────── */}
          <ReviewSection
            title="Agent suggestions"
            count={suggestions.length}
            open={open.suggestions}
            onToggle={() => setOpen((o) => ({ ...o, suggestions: !o.suggestions }))}
            empty="No pending agent suggestions."
          >
            {suggestions.map((s) => (
              <SuggestionCard
                key={s.id}
                suggestion={s}
                categoryName={categoryNameFor(categoryOptions, s)}
                focusKey={focusKey}
                onFocus={setFocusKey}
                register={register}
              />
            ))}
          </ReviewSection>

          {/* ─── Uncategorized ────────────────────────────────────────── */}
          <ReviewSection
            title="Uncategorized"
            count={uncategorizedGroups.length}
            open={open.uncategorized}
            onToggle={() => setOpen((o) => ({ ...o, uncategorized: !o.uncategorized }))}
            empty="Everything is categorized."
          >
            {uncategorizedGroups.map((group) => (
              <UncategorizedGroupCard
                key={group.key}
                group={group}
                categories={categoriesQ.data ?? []}
                focusKey={focusKey}
                onFocus={setFocusKey}
                register={register}
                onRuleSeed={(seed) => setRuleDialog(seed)}
              />
            ))}
          </ReviewSection>
        </div>
      )}

      <RuleFromCategorizationDialog
        seed={ruleSeedState[0]}
        categories={categoriesQ.data ?? []}
        onClose={() => setRuleDialog(null)}
      />
    </div>
  );
}

// ─── Collapsible section shell ────────────────────────────────────────────────

function ReviewSection({
  title,
  count,
  open,
  onToggle,
  empty,
  children,
}: {
  title: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  empty: string;
  children: React.ReactNode;
}) {
  return (
    <section className={styles.reviewSection}>
      <button type="button" className={styles.reviewSectionHead} onClick={onToggle} aria-expanded={open}>
        <span className={`${styles.twisty} ${open ? styles.twistyOpen : ''}`}>
          <Icon name="chevron" size={11} />
        </span>
        <span className={styles.reviewSectionTitle}>{title}</span>
        <span className={styles.countBadge}>{count}</span>
      </button>
      {open && (
        <div className={styles.reviewSectionBody}>
          {count === 0 ? <div className={styles.muted}>{empty}</div> : children}
        </div>
      )}
    </section>
  );
}

// ─── Transfer group card (with sibling chooser) ───────────────────────────────

interface TransferGroup {
  key: string;
  /** True when this leg has multiple candidate counterparts → chooser UI. */
  ambiguous: boolean;
  /** The shared leg description for the chooser header. */
  anchorLabel: string;
  options: FinanceTransferDto[];
}

function TransferGroupCard({
  group,
  focusKey,
  onFocus,
  register,
}: {
  group: TransferGroup;
  focusKey: string | null;
  onFocus: (key: string) => void;
  register: (key: string, action: ItemAction) => void;
}) {
  const confirm = useConfirmTransfer();
  const reject = useRejectTransfer();
  const key = `transfer:${group.key}`;
  const focused = focusKey === key;
  const busy = confirm.isPending || reject.isPending;

  // Single (unambiguous): one card with Confirm/Reject. Enter confirms, x rejects.
  // Ambiguous chooser: Enter confirms the FIRST option; x rejects the first.
  const primary = group.options[0];
  register(key, {
    primary: primary && !busy ? () => confirm.mutate(primary.id) : undefined,
    secondary: primary && !busy ? () => reject.mutate(primary.id) : undefined,
  });

  const error = financeErrorMessage(confirm.error) ?? financeErrorMessage(reject.error);

  return (
    <article
      className={`${styles.reviewCard} ${focused ? styles.reviewCardFocused : ''}`}
      onMouseEnter={() => onFocus(key)}
      tabIndex={-1}
    >
      {group.ambiguous && (
        <div className={styles.chooserHead}>
          <Icon name="link" size={12} /> Which is the counterpart for{' '}
          <strong>{group.anchorLabel}</strong>?
        </div>
      )}
      <div className={styles.transferOptions}>
        {group.options.map((t) => (
          <div key={t.id} className={styles.transferRow}>
            <div className={styles.transferLegs}>
              <TransferLeg label="From" leg={t.from} />
              <span className={styles.transferArrow}>
                <Icon name="arrowRight" size={14} />
              </span>
              <TransferLeg label="To" leg={t.to} />
            </div>
            <div className={styles.transferAmount}>{formatEur(t.amountEur)}</div>
            <div className={styles.rowActions}>
              <button
                type="button"
                className="kos-btn"
                disabled={busy}
                onClick={() => reject.mutate(t.id)}
              >
                Reject
              </button>
              <button
                type="button"
                className="kos-btn kos-btn-primary"
                disabled={busy}
                onClick={() => confirm.mutate(t.id)}
              >
                <Icon name="check" size={12} /> Confirm
              </button>
            </div>
          </div>
        ))}
      </div>
      {error && <div className={styles.fieldError}>{error}</div>}
    </article>
  );
}

function TransferLeg({ label, leg }: { label: string; leg: FinanceTransferDto['from'] }) {
  // Suggested transfers are always two-sided (the matcher never produces
  // one-sided rows), so `leg` is present here; guard defensively anyway.
  if (!leg) {
    return (
      <div className={styles.transferLeg}>
        <span className={styles.transferLegLabel}>{label}</span>
        <span className={styles.transferLegAccount}>—</span>
      </div>
    );
  }
  return (
    <div className={styles.transferLeg}>
      <span className={styles.transferLegLabel}>{label}</span>
      <span className={styles.transferLegAccount}>{leg.accountName}</span>
      <span className={styles.transferLegDesc}>{leg.description}</span>
      <span className={styles.transferLegMeta}>
        {leg.date} · {formatEur(leg.amountEur)}
      </span>
    </div>
  );
}

// ─── Agent suggestion card (client-orchestrated accept) ───────────────────────

function SuggestionCard({
  suggestion,
  categoryName,
  focusKey,
  onFocus,
  register,
}: {
  suggestion: FinanceSuggestionDto;
  categoryName: string | null;
  focusKey: string | null;
  onFocus: (key: string) => void;
  register: (key: string, action: ItemAction) => void;
}) {
  const patch = usePatchFinanceTransaction();
  const createTransfer = useCreateTransfer();
  const accept = useAcceptFinanceSuggestion();
  const reject = useRejectFinanceSuggestion();
  const [applyError, setApplyError] = useState<string | null>(null);

  const key = `suggestion:${suggestion.id}`;
  const focused = focusKey === key;
  const busy = patch.isPending || createTransfer.isPending || accept.isPending || reject.isPending;

  const isAlert = suggestion.kind === 'finance.alert';

  // Client-orchestrated accept: apply the finance change FIRST, then record-only
  // accept. On apply failure, leave the suggestion pending and surface the error.
  const doAccept = useCallback(() => {
    if (busy) return;
    setApplyError(null);

    const recordAccept = () => accept.mutate(suggestion.id);

    if (suggestion.kind === 'finance.categorize') {
      const categoryId = (suggestion.proposed as { categoryId?: unknown }).categoryId;
      if (typeof categoryId !== 'string') {
        setApplyError('Suggestion is missing a categoryId.');
        return;
      }
      patch.mutate(
        { id: suggestion.targetId, patch: { categoryId } },
        {
          onSuccess: recordAccept,
          onError: (err) => setApplyError(financeErrorMessage(err)),
        },
      );
    } else if (suggestion.kind === 'finance.transfer_match') {
      const p = suggestion.proposed as { fromTransactionId?: unknown; toTransactionId?: unknown };
      if (typeof p.fromTransactionId !== 'string' || typeof p.toTransactionId !== 'string') {
        setApplyError('Suggestion is missing the transfer leg ids.');
        return;
      }
      createTransfer.mutate(
        { fromTransactionId: p.fromTransactionId, toTransactionId: p.toTransactionId },
        {
          onSuccess: recordAccept,
          onError: (err) => setApplyError(financeErrorMessage(err)),
        },
      );
    } else {
      // finance.alert (or any other) — no finance call; just record the accept.
      recordAccept();
    }
  }, [busy, suggestion, patch, createTransfer, accept]);

  const doReject = useCallback(() => {
    if (!busy) reject.mutate(suggestion.id);
  }, [busy, reject, suggestion.id]);

  register(key, { primary: doAccept, secondary: doReject });

  const proposedSummary = useMemo(
    () => describeProposed(suggestion, categoryName),
    [suggestion, categoryName],
  );
  const acceptError = financeErrorMessage(accept.error);
  const rejectError = financeErrorMessage(reject.error);

  return (
    <article
      className={`${styles.reviewCard} ${focused ? styles.reviewCardFocused : ''} ${isAlert ? styles.alertCard : ''}`}
      onMouseEnter={() => onFocus(key)}
      tabIndex={-1}
    >
      <header className={styles.suggestionHead}>
        <span className={styles.suggestionKind}>
          <Icon name={isAlert ? 'bell' : 'sparkles'} size={12} /> {kindLabel(suggestion.kind)}
        </span>
        {suggestion.confidence && (
          <span className={styles.confidenceTag}>{suggestion.confidence}</span>
        )}
      </header>
      <p className={styles.suggestionRationale}>{suggestion.rationale}</p>
      {proposedSummary && <div className={styles.suggestionProposed}>{proposedSummary}</div>}

      <footer className={styles.suggestionFoot}>
        <div className={styles.rowActions}>
          <button type="button" className="kos-btn" disabled={busy} onClick={doReject}>
            {isAlert ? 'Dismiss' : 'Reject'}
          </button>
          <button type="button" className="kos-btn kos-btn-primary" disabled={busy} onClick={doAccept}>
            <Icon name="check" size={12} /> {isAlert ? 'Acknowledge' : 'Accept'}
          </button>
        </div>
      </footer>
      {(applyError || acceptError || rejectError) && (
        <div className={styles.fieldError}>{applyError ?? acceptError ?? rejectError}</div>
      )}
    </article>
  );
}

// ─── Uncategorized group card (bulk categorize + make-rule) ───────────────────

interface UncategorizedGroup {
  key: string;
  label: string;
  transactions: FinanceTransactionDto[];
}

function UncategorizedGroupCard({
  group,
  categories,
  focusKey,
  onFocus,
  register,
  onRuleSeed,
}: {
  group: UncategorizedGroup;
  categories: FinanceCategoryDto[];
  focusKey: string | null;
  onFocus: (key: string) => void;
  register: (key: string, action: ItemAction) => void;
  onRuleSeed: (seed: RuleSeed) => void;
}) {
  const bulk = useBulkCategorizeTransactions();
  const [categoryId, setCategoryId] = useState('');
  const key = `uncat:${group.key}`;
  const focused = focusKey === key;
  const n = group.transactions.length;

  const apply = useCallback(() => {
    if (!categoryId || bulk.isPending) return;
    bulk.mutate(
      { transactionIds: group.transactions.map((t) => t.id), categoryId },
      {
        onSuccess: () => {
          // Offer to turn this into a rule (FIN-10 affordance).
          onRuleSeed({ description: group.label, categoryId });
          setCategoryId('');
        },
      },
    );
  }, [categoryId, bulk, group, onRuleSeed]);

  // Enter applies (when a category is chosen); no secondary action.
  register(key, { primary: apply });

  const total = group.transactions.reduce((s, t) => s + t.amountEur, 0);
  // Group members share a normalized description; derive kind from the first
  // transaction's sign so only valid categories are offered.
  const groupKind = group.transactions[0]!.amount < 0 ? 'expense' : 'income';

  return (
    <article
      className={`${styles.reviewCard} ${focused ? styles.reviewCardFocused : ''}`}
      onMouseEnter={() => onFocus(key)}
      tabIndex={-1}
    >
      <div className={styles.uncatHead}>
        <span className={styles.uncatLabel}>{group.label}</span>
        <span className={styles.uncatMeta}>
          {n} transaction{n === 1 ? '' : 's'} · {formatEur(total)}
        </span>
      </div>
      <div className={styles.uncatActions}>
        <div className={styles.uncatPicker}>
          <CategoryPicker
            categories={categories}
            value={categoryId || null}
            kind={groupKind}
            placeholder="Categorize as…"
            ariaLabel={`Category for ${group.label}`}
            onChange={(catId) => setCategoryId(catId)}
          />
        </div>
        <button
          type="button"
          className="kos-btn kos-btn-primary"
          disabled={!categoryId || bulk.isPending}
          onClick={apply}
        >
          {bulk.isPending ? 'Applying…' : `Apply to group (${n})`}
        </button>
      </div>
      {bulk.isError && <div className={styles.fieldError}>{financeErrorMessage(bulk.error)}</div>}
    </article>
  );
}

// ─── Pure helpers ─────────────────────────────────────────────────────────────

/** Group suggested transfers so multiple candidates for the same leg collapse
 *  into one chooser card. Keyed by the leg that appears more than once; a leg
 *  with a single candidate stands alone. */
function groupTransfersByLeg(transfersIn: FinanceTransferDto[]): TransferGroup[] {
  // Suggested transfers are always two-sided (the matcher never produces
  // one-sided/external rows). Narrow to two-sided rows so the leg accesses
  // below are type-safe; any (impossible) one-sided suggestion is dropped.
  type TwoSided = FinanceTransferDto & {
    from: NonNullable<FinanceTransferDto['from']>;
    to: NonNullable<FinanceTransferDto['to']>;
  };
  const transfers = transfersIn.filter(
    (t): t is TwoSided => t.from !== null && t.to !== null,
  );

  // Count how many transfers each leg id participates in.
  const legCount = new Map<string, number>();
  for (const t of transfers) {
    legCount.set(t.from.id, (legCount.get(t.from.id) ?? 0) + 1);
    legCount.set(t.to.id, (legCount.get(t.to.id) ?? 0) + 1);
  }

  const used = new Set<string>(); // transfer ids already placed in a group
  const groups: TransferGroup[] = [];

  for (const t of transfers) {
    if (used.has(t.id)) continue;
    // Find the leg (if any) shared by multiple transfers → chooser anchor.
    const fromShared = (legCount.get(t.from.id) ?? 0) > 1;
    const toShared = (legCount.get(t.to.id) ?? 0) > 1;

    if (fromShared || toShared) {
      const anchorId = fromShared ? t.from.id : t.to.id;
      const anchorLeg = fromShared ? t.from : t.to;
      const options = transfers.filter(
        (o) => !used.has(o.id) && (o.from.id === anchorId || o.to.id === anchorId),
      );
      for (const o of options) used.add(o.id);
      groups.push({
        key: anchorId,
        ambiguous: options.length > 1,
        anchorLabel: `${anchorLeg.description} (${anchorLeg.accountName})`,
        options,
      });
    } else {
      used.add(t.id);
      groups.push({
        key: t.id,
        ambiguous: false,
        anchorLabel: t.from.description,
        options: [t],
      });
    }
  }
  return groups;
}

/** Normalize a description for grouping: trim, collapse whitespace, lowercase. */
function normalizeDescription(description: string): string {
  return description.trim().replace(/\s+/g, ' ').toLowerCase();
}

function groupByNormalizedDescription(transactions: FinanceTransactionDto[]): UncategorizedGroup[] {
  const map = new Map<string, FinanceTransactionDto[]>();
  for (const t of transactions) {
    const k = normalizeDescription(t.description);
    const arr = map.get(k) ?? [];
    arr.push(t);
    map.set(k, arr);
  }
  return Array.from(map.entries())
    .map(([k, txns]) => ({
      key: k,
      // Display the first transaction's original (un-lowercased) description.
      label: txns[0]!.description,
      transactions: txns,
    }))
    .sort((a, b) => b.transactions.length - a.transactions.length);
}

function kindLabel(kind: string): string {
  switch (kind) {
    case 'finance.categorize':
      return 'Categorize';
    case 'finance.transfer_match':
      return 'Transfer match';
    case 'finance.alert':
      return 'Alert';
    default:
      return kind;
  }
}

function categoryNameFor(
  options: CategoryOption[],
  suggestion: FinanceSuggestionDto,
): string | null {
  const categoryId = (suggestion.proposed as { categoryId?: unknown }).categoryId;
  if (typeof categoryId !== 'string') return null;
  return options.find((o) => o.id === categoryId)?.label.trim() ?? null;
}

function describeProposed(suggestion: FinanceSuggestionDto, categoryName: string | null): string | null {
  if (suggestion.kind === 'finance.categorize') {
    return categoryName ? `Categorize as ${categoryName}` : 'Categorize this transaction';
  }
  if (suggestion.kind === 'finance.transfer_match') {
    return 'Link these two transactions as a transfer';
  }
  return null;
}
